import type { DateObject } from '../calendar-date-serivce/calendar-date-service';

const SUPPORT_NUMBER = '(888) 380-2028';
const HELP_EMAIL = 'help@joinuplift.co';
const HIGHER_ED_EMAIL = 'oncampus@joinuplift.co';

export const l10nEnService = {
  helpEmail: HELP_EMAIL,
  validations: {
    emergencyName: 'Please enter the name of your emergency contact',
    emergencyPhone: 'Please enter a valid phone number',
    requiredEmergencyPhone: 'You need to enter a phone number',
    notRelevantDate: 'Not relevant date',
    handicapAccessibleError: 'Clients need to know if this location is handicap accessible',
    couldContainEnglish: 'Could contain letters in English, digits, some special characters',
    couldContainEnglishWithSpace: 'Could contain letters in English, digits, some special characters or space',
    emailNotAllowedInProviderProfile: 'Emails are not allowed in provider profiles',
    weblinksNotAllowedInProviderProfile: 'Weblinks are not allowed in provider profiles',
    nameMatches: "Make sure your name doesn't contain numbers or special characters like ! # $ % & * + / = ? ^ _ ` { |",
    enterFirstName: 'Enter a first name for your profile',
    enterLastName: 'Enter a last name for your profile',
    newClientFirstName: 'Enter a first name for new client',
    newClientLastName: 'Enter a last name for new client',
    requiredPass: 'You need to enter a password',
    passwordMax: 'Your password needs to be less than 125 characters',
    invalidEmail: 'This email is invalid. Make sure it is written like example@email.com',
    validEmailOnly: 'Please use only standard English letters in your email address',
    unchangedEmail: 'This email is unchanged. To update, enter a new email.',
    requiredMentalHealth: 'You need to select your mental health role',
    requiredLicenseState: 'You need to select your license state',
    requiredLicenseNumber: 'Enter your license number',
    requiredLicenseExpiration: 'You need to enter your license expiration',
    requiredEmail: 'You need to enter an email',
    insurance: 'You need to choose an insurance',
    selectInsurance: 'Select your insurance',
    insuranceProviderName: 'Enter the name of your insurance provider',
    insuranceMemberId: 'Enter your member ID',
    insuranceCardFront: 'You need to upload the front of your card',
    insuranceCardBack: 'You need to upload the back of your card',
    primarySubFirstName: 'Enter the primary subscriber’s first name',
    primarySubLastName: 'Enter the primary subscriber’s last name',
    primarySubStreetAddress: 'Enter the primary subscriber’s street address',
    primarySubCity: 'Enter the primary subscriber’s city',
    primarySubState: 'Enter the primary subscriber’s state',
    primarySubZip: 'Enter the primary subscriber’s ZIP code',
    requiredStreetAddress: 'You need to enter your street address',
    requiredCity: 'You need to enter your city',
    requiredState: 'You need to enter your state',
    requiredZip: 'You need to enter your 5-digit ZIP Code',
    requiredWorkNumber: 'Enter your work number',
    requiredMobilePhone: 'Enter mobile phone number',
    requiredCancelationReason: 'You need to select a cancelation reason',
    emailTaken: 'This email is already connected to an account. <a class="red-color-link" href="https://login.joinuplift.co/auth/login">Sign in</a>.',
    required: 'Mandatory field',
    tooShort: 'Too short',
    tooLong: 'Too Long',
    tooLongIndicator: (maxLength: number, currentLength: number) => `Too Long (${currentLength}/${maxLength})`,
    lengthMatches: (min: number, max: number) => `The number of characters need to be between ${min} and ${max}`,
    lengthEqual: (length: number) => `The number of characters must be ${length}`,
    licenseNumber: 'You must enter your license number',
    passwordsDontMatch: 'Passwords do not match',
    imageTooBig: 'Maximum image size is 10 Mbyte',
    imageWidthTooBig: 'Minimum image width is 250px',
    invalidFileType: 'Invalid file type',
    digitsOnly: 'The field should have digits only',
    validYear: 'Enter year as YYYY',
    validLicenceExpiration: 'Enter valid date as MM/YYYY',
    phoneMatches: 'Enter a valid 10-digit phone number',
    passwordMatches: 'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character',
    requiredYearGraduated: 'Enter your graduation year',
    requiredMoreInformation: 'More information about you and your approach is required for your profile ',
    sex: 'You must enter your sex',
    practice: 'Enter years in practice for your profile',
    practiceExpiration: 'Expiration date can not be in the past',
    yearsInPracticeMax: 'Must be less than 100 years',
    enterSchool: 'Enter you school for your profile',
    degree: 'Select your degree',
    study: 'Enter you field of study for your profile',
    years: 'Enter year as YYYY',
    photoUpload: 'You need to upload a photo for your profile',
    termsOfService: 'You need to acknowledge that you have read and accept the terms of service',
    lowercaseLetter: 'A lowercase letter',
    uppercaseLetter: 'An uppercase letter',
    aNumber: 'A number',
    specialCharacter: 'A special character',
    minLength: (minLength: number) => `${minLength} characters minimum`,
    isSecure: 'Your password is secure',
    chooseEndTime: 'Choose an end time',
    chooseEndTimeLaterThanStartTime: 'Choose an end time later than the start time',
    profanityNotAllowedInProviderProfiles: 'Profanity is not allowed in provider profiles',
    loginForm: {
      emailBlank: 'Please enter your email address',
      noAt: 'An email address must contain a single @',
      noDomain: 'This email address is invalid. Check the portion after the @',
      passwordBlank: 'Please enter your password',
    },
    dob: {
      notAValidDate: 'Not a valid date',
      required: 'Date of birth is required',
      ageLessThan100: 'Your age must be less than 100',
      atLeast18: 'You must be at least 18 to use UpLift yourself',
      dateInThePast: 'Enter a date in the past',
    },
    client: {
      nameOnCard: {
        empty: 'Enter the name on the card',
        tooLong: 'Names must be shorter than 64 characters',
      },
      cardNumber: {
        empty: 'Enter valid credit card number',
        invalid: (length: 15 | 16) => `Enter a valid ${length}-digit card number`
      },
      cvc: {
        empty: 'Enter your CVC',
        invalid: (length: 3 | 4) => `Enter your ${length}-digit CVC`,
      },
      expiration: {
        emptyInvalid: 'Enter a valid expiration date as MM/YY',
        inPast: 'This date has passed. Enter a current card.',
      },
      address: {
        line1Empty: 'Enter your street address',
        line1TooLong: 'Street addresses must be shorter than 50 characters',
        cityEmpty: 'Enter your city',
        cityTooLong: 'City names must be shorter than 50 characters',
        state: 'Select your state',
        zipEmpty: 'Enter your 5-digit ZIP code'
      },
      practiceAddress: {
        line1Empty: 'Enter a street address for your practice',
        cityEmpty: 'Enter your practice’s city',
        state: 'Enter your practice’s state',
        zipEmpty: 'Enter your practice’s ZIP code',
        displayStreetAddress: 'Choose whether clients can see your street address'
      }
    },
    enterInstitutionName: 'Enter institution name',
    selectState: 'Select a state'
  },
  admin: {
    createNew: 'Create New',
    notes: {
      noNotes: 'No notes yet',
      noNotesMatchFilters: 'No notes match the current filters',
    },
    states: {
      collabSupervisorCapacityNotice: 'Max 99. When a supervising physician reaches the cap, new matches in this state are blocked.'
    },
    reports: {
      users: {
        user: 'User',
        role: 'Role',
        status: 'Status',
        lastLogIn: 'Last Log In',
        createdAt: 'Created At'
      },
      appointment: {
        createdAt: 'Session Created',
        patientName: 'Patient',
        providerName: 'Provider Name',
        fromDatetime: 'Session Time',
        paymentMethod: 'Payment Method',
        cashPay: 'Cash Pay',
        appointmentStatus: 'Status'
      },
      sessionHistory: {
        dateOfService: 'Date of Service',
        providerDateOfService: 'Date & Time',
        provider: 'Provider',
        patient: 'Client',
        description: 'Description',
        appointmentStatus: 'Appointment Status',
        providerEarnings: 'Provider Earnings',
        cptCodes: 'CPT Codes',
        sessionType: 'Session Type',
        cash: 'Cash',
        insurance: 'Insurance',
        covered: 'Covered',
        payment: 'Payment',
        claimStatus: 'Claim Status',
        balance: 'Balance',
        id: 'ID',
        sessionHistorySearch: 'Search for an appointment ID or date of service'
      },
      activityFeed: {
        header: 'Activity',
        notSynced: 'We’re unable to sync activity from the old payment system. For more details, contact the billing team.',
        submittedNote: (name: string, noteType: string) => `${name} submitted ${noteType} note`,
        chargeAdded: 'Charge added',
        canceledSession: (name: string) => `${name} canceled the session`,
        refundedLateFee: 'refunded late fee',
        heldForReview: 'held session charge for review',
        lateFeeRefunded: (lateFee: string) => `• $${lateFee} late fee refunded`,
        paymentReceived: 'Payment received',
        withLateFee: 'With late fee',
        noLateFee: 'No late fee'
      },
      refund: {
        refundThroughStripe: (userName: string, amount: string) => `${userName} will receive a refund of ${amount} through Stripe.`,
        feeSubtractedFromPayout: (providerName: string) => `$50 will be subtracted from ${providerName}'s payout.`,
        changesToLateFees: 'Changes to late fees can be done once within 7 days',
        refundLateFee: 'Refund Late Fee',
        reasonForRefunding: 'Reason for refunding late fee'
      },
    },
    inviteSentTo: (userEmail: string) => `Invite sent to ${userEmail}`,
    inviteCouldNotBeSend: 'Invite could not be sent',
    inviteProvider: {
      resendInvite: 'Resend Invite'
    },
    profile: {
      insuranceCredentials: {
        rosterStatus: 'Roster Status',
        selectRosterStatus: 'Select roster status'
      },
      licenses: {
        deleteText: 'Delete License',
        deleteConfirmText: (state: string, role: string) => {
          return `If you remove this license, the provider will no longer be eligible to match with clients in ${state} looking for ${role}.`;
        }
      },
      chips: {
        betterhelp: 'Betterhelp',
        betterhelpUTM: 'Betterhelp UTM',
        betterhelpEAP: 'Betterhelp EAP',
        betterhelpBH40: 'Betterhelp BH40',
      }
    }
  },
  navigation: {
    signIn: 'SIGN IN',
    learnMore: 'Learn More',
    back: 'Back',
    backToEdit: 'Back to Edit',
    logout: 'Log out',
    exit: 'Exit',
    next: 'Next'
  },
  auth: {
    signBackIn: 'Sign back in',
    notFound: 'We can’t find the page you’re looking for.',
    subText: 'Let’s get you back on track and go to the homepage.',
    goto: 'Go to home',
    error: '404 Error',
    forgotPassword: 'Forgot Password?',
    email: 'Email address',
    passwordReset: 'Send Password Reset',
    change: 'Change Password',
    signIn: 'Sign In',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    logout: 'Logout',
    reset: 'Reset password',
    resetText: 'Please enter a new password for your account.',
    forgotText: 'Enter in the email you use to sign in and we’ll send a link to reset your password.',
    rememberYourPassword: 'Remember your password?',
    newToUplift: 'New to UpLift?',
    checkYourEmail: 'Check your email',
    magicLink: (email: string) => `We emailed a magic link to ${email}`,
    errors: {
      youWereSignedOut: {
        title: 'You have been logged out',
        details: 'We take the confidentiality and privacy of personal information seriously. You’re automatically logged out if the browser is left inactive for a period of time to ensure session information and data is protected.',
      },
      invalidEmailOrPassword: {
        title: 'Invalid email or password',
        details: 'Please double check your information and try again',
      },
      userIsNotActive: {
        title: 'Inactive account',
        details: `Please email ${HELP_EMAIL} for support`,
      },
      unknown: {
        title: 'Issue with internet connection',
        details: `Please try again or contact ${HELP_EMAIL} for support`,
      }
    },
    forgotPasswordInfo: {
      ifWeFoundYourAccount: 'If we’ve found your account, you’ll receive an email link with instructions.',
      dontSeeAnEmail: 'Don’t see an email after a few minutes or need additional help? Contact us at ',
      supportEmail: HELP_EMAIL,
    },
    mfa: {
      whereShouldWeSend: 'Where should we send your code?',
      textIt: 'Text it to',
      emailIt: (email: string) => `Email it to ${email}`,
      sendCode: 'send code',
      emailedCode: 'Enter the 4-digit code sent to',
      textedCode: 'Enter the 4-digit code texted to',
      verifyYourIdentity: 'Verify your identity',
      snooze2fa: "Don't ask to verify for 30 days",
    }
  },
  pages: {
    providerMatchingFieldsReport: 'Provider matching',
    providerUnsubmittedSessions: 'Unsubmitted Sessions',
    providerCancelledSessions: 'Cancelled Sessions',
    messaging: 'Messaging',
    clientOutstandingBalnce: 'Outstanding Balances',
    matchingView: 'Match With Client',
    reports: 'Reports',
    reportsPast: 'Past sessions',
    reportsUpcoming: 'New Clients',
    main: 'Home',
    unsubmittedSessions: 'Unsubmitted Sessions',
    clients: 'Clients',
    supervisees: 'Supervisees',
    yourCareTeam: 'Your Care Team',
    contactSupport: 'Contact Support',
    settings: 'Settings',
    accountSettings: 'Account Settings',
    messages: 'Messages',
    support: 'Support',
    users: 'Users',
    higherEducation: 'Higher Education'
  },
  general: {
    help: 'Help',
    helpCenter: 'Help Center',
    download: 'Download',
    isActive: 'User state',
    error: 'Something went wrong',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    email: 'Email',
    optional: 'Optional',
    details: 'Details',
    dismiss: 'Dismiss',
    close: 'Close',
    cancel: 'Cancel',
    submit: 'Submit',
    viewProfile: 'View profile',
    no: 'No',
    on: 'On',
    off: 'Off',
    edit: 'Edit',
    gotIt: 'Got it',
    tools: 'management',
    viewMore: 'view more',
    client: 'Client',
    clear: 'Clear',
    apply: 'Apply',
    save: 'Save',
    autoLogout: {
      modalNotification: {
        title: 'Still working?',
        content: 'Privacy and security is important to us and you’ve been gone a while. We’ll log you out soon unless confirmed otherwise.',
        logout: 'Log out',
        stayLoggedIn: 'Stay logged in',
      }
    },
    weekday: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
    weekdayPlural: [
      'Sundays',
      'Mondays',
      'Tuesdays',
      'Wednesdays',
      'Thursdays',
      'Fridays',
      'Saturdays',
    ],
    ok: 'Ok',
    none: 'None',
    add: 'Add',
    welcome: 'Welcome',
    account: 'Account',
  },
  yourCareTeam: {
    archivedProviders: {
      archivedOn: 'Archived on',
      show: (providersCount: number) => `Show Archived Providers (${providersCount})`,
      hide: (providersCount: number) => `Hide Archived Providers (${providersCount})`,
    }
  },
  dashboard: {
    welcomeBack: 'Welcome back.',
    tabs: {
      today: 'Today',
      toDo: 'To Do',
      payments: 'Payments',
      sharePublicProfileUrl: 'Share Profile',
    },
    formProgress: {
      referral: (careType: string) => `Referral for ${careType}`,
      intake: 'Intake',
      phq9: 'PHQ-9',
      gad7: 'GAD-7',
      noNote: 'No note',
      incomplete: 'Incomplete',
      completed: 'Completed'
    },
    toDo: {
      items: (itemsLength: number) => `Item${itemsLength !== 1 ? 's' : ''}`,
      numberOfItems: (itemsLength: number) => `${itemsLength} Item${itemsLength !== 1 ? 's' : ''}`,
      toDo: 'To Do',
      nothingToDo: 'Nothing to do'
    },
    actionItems: {
      status: {
        todo: 'To Do',
        inProgress: 'In Progress',
        inReview: 'In Review',
        approved: 'Approved',
        done: 'Complete',
      },
      empty: {
        title: 'No action items',
        subtitle: "Well done—you're all caught up!",
      },
      intakeForm: {
        title: (percents: number) => `Your intake form is ${percents}% complete`
      },
      intakeFormEmpty: {
        title: 'Fill out your <b>intake form</b> to help you and your provider prepare for your first session'
      },
      assessment: {
        title: "<b>Check in with yourself</b> and reflect on how you've been feeling the past 2 weeks"
      },
      cardExpired: {
        title: 'Your <b>payment card</b> on file <b>has expired</b>',
        subtitle: 'Update it before your next session',
      },
      cardExpiration: {
        title: 'Your <b>payment card</b> on file is <b>about to expire</b>',
        subtitle: (expirationDate: string) => `Update it before ${expirationDate}`,
      },
      emergencyContact: {
        title: 'Add an <b>emergency contact</b> for your Care Team'
      },
      mobilePhoneVerification: {
        title: 'Sign up for <b>text reminders</b> and avoid cancellation fees'
      },
      inviteClients: {
        title: '<b>Invite clients</b> to use UpLift sessions',
        dismissText: 'Not Now',
        tooltipTitle: 'Cross an item off your list',
        tooltipScheduleSessionsWithUpLift: "Now that you're live, you can use UpLift for sessions with your existing clients - and even accept their insurance.",
        tooltipInviteCurrentClient: 'While you wait for a match with a new client, <b>try inviting a current one</b> and schedule a session with them.',
      },
      stateSelection: {
        title: 'Add the <b>states</b> where you want to practice',
        subtitle: 'Est time to complete: <b>1</b> min',
      },
      setupMatchingProfile: {
        title: 'Set up your <b>Matching Profile</b>',
        subtitle: 'Est time to complete: <b>20</b> min',
      },
      requiredTrainings: {
        title: 'Complete required trainings',
        pending: 'Your required trainings are being set up and will appear here soon.',
        progress: (completed: number, total: number) => `${completed} of ${total} completed`,
        lastSynced: (timeAgo: string) => `Last synced ${timeAgo}`,
        showTrainings: 'Show trainings',
        hideTrainings: 'Hide trainings',
        startCta: 'Start',
        continueCta: 'Continue',
        completedOn: (date: string) => `Completed ${date}`,
        estimatedTime: (minutes: number) => `Est. time to complete: ${minutes} min`,
        annual: 'Annual',
        oneTime: 'One-time',
        aggregateStatus: {
          notStarted: 'Not started',
          inProgress: 'In progress',
          completed: 'Completed',
        },
      },
      addMatchingAvailability: {
        title: 'Add <b>Matching Availability</b>',
        subtitle: 'Est time to complete: <b>5</b> min',
      },
      payorEnrollment: {
        title: 'Set up your <b>Payer Enrollment</b>',
        subtitle: 'Est time to complete: <b>5</b> min',
        buttonText: 'Set Up',
        progressLabel: 'Payer Enrollment',
      },
      addMfa: {
        title: 'Set up <b>2 factor authentication</b>',
        subtitle: 'Est time to complete: <b>5</b> min',
        buttonText: 'Set up',
      },
      unsubmittedSessions: {
        title: (unsubmittedCount: number) => `You have <b>${unsubmittedCount}</b> unsubmitted session${unsubmittedCount > 1 ? 's' : ''}`,
        buttonText: (count: number) => `Submit Session${count > 1 ? 's' : ''}`
      },
      clientScheduledPsych: {
        title: (name: string, date: string, time: string) => `<b>${name}</b> scheduled their first <b>psychiatry session</b> for ${date} at ${time}`,
        subtitle: (date: string, time: string) => `Next therapy session on ${date} at ${time}`,
        body: (name: string) => `Send ${name} a message to let them know you're with them every step of the way.`,
        actionText: 'Send message',
        dismissText: 'Dismiss',
        modal: {
          title: (name: string) => `Check in with ${name}`,
          subtitle: 'Feel free to personalize this premade message or use it as is.',
          label: (name: string) => `Your message to ${name}`,
          emailTemplate: (name: string) =>
            `Hi ${name}—Saw that you scheduled your first psychiatry session and wanted to reach out. It probably wasn’t an easy decision to make but it shows your growth and dedication to your mental well-being.
I’ll be with you throughout this process as you adjust. Let’s talk about it more next session and how psychiatry can help you access skills in your personal toolbox.
`,
        }
      },
      newPsychNote: {
        title: (psychName: string, clientName: string) => `<b>${psychName}</b> added a new <b>psychiatry progress note</b> to <b>${clientName}'s</b> chart`,
        subtitle: (date: string, time: string) => `Next therapy session on ${date} at ${time}`,
        actionText: 'Review Note',
        dismissText: 'Dismiss',
      },
      newClient: {
        title: (clientName: string) => `New client: ${clientName}`,
        subtitle: (date: string, time: string) => `First session on ${date} at ${time}`,
        requiresAttention: 'Requires Attention',
        recommendConsultation: 'Responses suggest that your client could benefit from a psychiatric consultation.',
        actionText: 'Review chart',
        dismissText: 'Mark as viewed',
        expandText: 'Show Forms',
        hideText: 'Hide Forms',
      },
      partnerReferral: {
        title: (clientName: string) => `Partner Referral: ${clientName}`,
      },
      referral: {
        title: (clientName: string) => `Referral: ${clientName}`
      },
      patientFilledAssessment: {
        title: (clientName: string, forms: string) => `<b>${clientName}</b> completed a ${forms}`,
        highRiskTitle: (patientName: string) => `${patientName}'s latest assessment responses require attention`,
        subtitle: (date: string, time: string) => `Next session on ${date} at ${time}`,
        highRiskSubtitle: (date: string, time: string) => `Next therapy session on ${date} at ${time}`,
        noReferRecommendConsultation: (name: string) => `${name}'s scores require attention. Review their assessment answers to understand their symptoms.`,
        highRiskBody: 'Responses suggest that your client could benefit from a psychiatric consultation. Consider sending them a referral to schedule time with a psychiatrist.',
        actionText: 'Review Results',
        dismissText: 'Mark as viewed',
        refer: 'Refer to a psychiatrist',
        notNow: 'Not now',
        modal: {
          title: (name: string) => `Refer ${name} to a psychiatrist`,
          subtitle: "When you refer a client, they'll get a message from you and a link that lets them schedule a consultation. You can use this premade message—but feel free to edit it with any information that might be helpful for your client.",
          emailTemplate: (name: string) =>
            `Hi ${name}—I reviewed your assessments and think that psychiatry could help you get to your goals. Let's start by having you schedule a psychiatric consultation and talk about it in your next session.`,
          formLabel: (name: string) => `Your message to ${name}`,
          submit: 'Send referral',
          cancel: 'Cancel'
        },
        deferModal: {
          title: (name: string) => `Why wasn't ${name} referred to psychiatry?`,
          reasons: [
            'I want to talk to my client before referring',
            "I don't think this client needs psychiatric care",
            'Client already has a psychiatrist',
            'Client has already expressed disinterest in psychiatry',
            'Client is unsure if medication will be helpful',
            'Client had a negative experience with medication before',
            "Psychiatry is out of the client's budget",
            'Client is using Medicaid/Medicare',
            "UpLift doesn't offer the necessary treatments",
          ],
          cancel: 'Cancel',
          send: 'Send',
        }
      },
      seePsych: {
        schedule: 'schedule a consultation',
        maybeLater: 'maybe later',
        dontShowAgain: "don't show again",
        initialTitle: 'Book your Psychiatry Consultation',
        title: "It's okay to need extra help",
        initialText: 'UpLift has psychiatric providers that work with you and your therapists to make sure you receive the best mental health care possible.',
        bothTitle: 'Continue with medication management scheduling',
        bothText: "You're all set for therapy and ready to schedule your first consultation session for medication management.",
        text: (phq9text?: string, gad7text?: string) => {
          if (phq9text && gad7text) {
            return `Your responses to your assessments suggest you are experiencing symptoms of ${phq9text} and ${gad7text}. A combination of talk therapy and medication could improve your symptoms. Talk to a psychiatric provider about your symptoms and see your options.`;
          }
          if (phq9text) {
            return `Your responses to your assessments suggest you are experiencing symptoms of ${phq9text}. A combination of talk therapy and medication could improve your symptoms. Talk to a psychiatric provider about your symptoms and see your options.`;
          }
          if (gad7text) {
            return `Your responses to your assessments suggest you are experiencing symptoms of ${gad7text}. A combination of talk therapy and medication could improve your symptoms. Talk to a psychiatric provider about your symptoms and see your options.`;
          }
        },
        assessments: {
          phq9: {
            moderate: 'moderately severe depression',
            severe: 'severe depression'
          },
          gad7: {
            moderate: 'moderately severe anxiety',
            severe: 'severe anxiety',
          },
        }
      },
      seeTherapist: {
        title: 'Build better, healthier habits',
        text: 'Psychiatry works best when combined with therapy. While medication can ease symptoms, therapy delves deeper to address the underlying emotional, psychological, and behavioral patterns that hold you back.',
        matchButton: 'match with a therapist',
        dismissButton: 'maybe later',
      },
      outstandingBalance: {
        title: (balance: string | number) => `You have an <b>outstanding balance</b> of <b>$${balance}</b>`,
        text: 'Pay it off before your next session',
        actionText: 'Pay balance',
      },
      verifyEmail: {
        title: 'Verify your <b>email address</b> for extra security',
        actionText: 'Verify',
        modalTitle: 'Check your inbox',
        modalText1: 'We’ve sent you a verification link to complete within the next 72 hours.',
        modalText2: `If you don't receive an email, contact ${HELP_EMAIL}.`
      }
    },
    appointmentRecurring: {
      none: (weekday: string) => `${weekday}`,
      weekly: (weekday: string) => `Every ${weekday}`,
      biweekly: (weekday: string) => `Every other ${weekday}`,
      monthly: (weekday: string) => `Every 4 weeks on ${weekday}`,
    },
    emailVerification: {
      verified: '<b>Email Verified.</b> Thanks for helping us keep your account secure',
      expired: 'Verification Link Expired',
      pleaseRequest: 'Please request a new link and complete verification within 72 hours.',
      requestNew: 'Request a new link',
      checkYourEmail: 'Check your inbox for an email from UpLift',
    }
  },
  sessionHistory: {
    noSessionHistory: {
      title: 'You haven’t had any sessions yet',
      subtitle: 'Find time with someone who cares about you. Email your provider to schedule a new session.',
      findATherapist: 'Find a therapist',
    }
  },
  clinicalNotes: {
    progressNote: 'Progress Note',
    progressNoteWithDate: (date: string) => `Progress Note (${date})`,
    selectTemplate: 'Select A Template',
    details: 'Details',
    identityVerification: 'Client identity and informed consent',
    diagnosis: 'Diagnosis',
    mentalStatusExam: 'Mental Status Exam',
    presentingProblems: 'Presenting Problems',
    plan: 'Plan',
    progress: 'Progress',
    riskAssessment: 'Risk Assessment',
    treatment: 'Treatment',
    cptCodes: 'CPT Codes',
    correctTheErrorsBelow: `Correct the errors below and try again. Email ${HELP_EMAIL} if you have any questions.`,
    thereWasAnError: (msg?: string) => `There was an error. Try again or email ${HELP_EMAIL} for help. ${msg ? `Error: ${msg}` : ''}`,
    participants: 'Participants',
    location: 'Location',
    addDiagnosis: 'Add Diagnosis',
    diagnosisSearchTerms: 'Diagnosis Search Terms',
    allWithinNormalRange: 'All within normal range?',
    recommendationConfirmation: (recommendation: string) => `By checking this box, I confirm that I will ${recommendation}.`,
    denyRisk: (patientName: string) => `Does ${patientName} deny all areas of risk with no contrary clinical indications present?`,
    delete: 'Delete',
    addApproach: 'Add Approach',
    assignHomework: 'Did you assign homework?',
    homework: 'Homework',
    approach: 'Approach',
    selectApproach: 'Select Approach',
    interventions: 'Intervention(s)',
    selectInterventions: 'Select all interventions that apply',
    noInterventionsForThisTreatment: 'No interventions for this treatment',
    selectApproachToSeeInterventionOptions: 'Select approach to see intervention options',
    suggestedCptCodes: 'Suggested CPT Codes',
    mostCommonCptCodes: 'Most common CPT Codes',
    otherCptCodes: 'Other CPT Codes',
    cptCode: 'CPT Code',
    saveAndFinishLater: 'Save And Finish Later',
    submitSessionNotes: 'Sign and Submit',
    followUp: 'Follow-up',
    clientScheduleNextAppointment: 'Client will reach out to schedule next appointment',
    nextAppointment: (date: string, time: string, meridiem?: string) => `${date} at ${time}${meridiem ? ` ${meridiem}` : ''}`,
    noAssessments: 'No assessments to attach',
    addProgressNotes: 'Add Progress Notes',
    additionalClinicalDetails: 'Additional Clinical Details',
    clinicalSummary: 'Clinical Summary',
    goals: 'Goals',
    objectives: 'Objectives',
    frequencyOfTreatment: 'Prescribed frequency of treatment',
    referrals: 'Referrals',
    treatmentGoals: 'Treatment Goals',
    recommendation: 'Recommendation',
    mentalStatus: 'Mental Status',
    clientDeniesRisk: 'The client denies all areas of risk with no contrary clinical indications present.',
    dateOfService: 'Date of Service',
    duration: 'Time and duration',
    provider: 'Provider',
    close: 'Close',
    noteStoredOffPlatform: 'Note was stored off platform',
    none: 'none',
    completedAt: (completedAt: string) => `Completed ${completedAt}`,
    submittedByOn: (providerName: string, providerCredential: string, date: string, time: string, meridiem: string) => `Electronically signed by ${providerName}${providerCredential ? `, ${providerCredential}` : ''} on ${date}, ${time} ${meridiem}`,
    patientProgressTowardsGoals: (patientName: string) => `${patientName} shows progress towards their goals.`,
    startTime: 'Start Time',
    endTime: 'End Time',
    timeShouldReflect: 'Time should only reflect when <b>both you and your client</b> were in the session',
    reminder: 'Reminder',
    reminderText: {
      line1: 'For the session to be covered by your client’s insurance, there must be',
      line2: 'at least one F code attached to the session note',
    },
    validations: {
      chooseAnOption: 'Please choose an option',
      cptCode: 'Please select a CPT code',
      diagnosis: 'Please add at least one diagnosis',
      diagnosisFCode: 'Add at least 1 F code',
      treatmentApproach: 'Please select an approach',
      treatmentIntervention: 'Select at least one intervention',
      homework: 'Please describe the homework',
      endTimeAfterStartTime: 'End time must be after start time'
    },
    addenda: {
      startAndEndTime: 'Start and End Time',
    }
  },
  user: {
    firstName: 'First Name',
    lastName: 'Last Name'
  },
  sex: {
    male: 'Male',
    female: 'Female',
  },
  time: {
    min: 'min'
  },
  calendar: {
    date: 'Date',
    metrics: {
      title: {
        daily: 'Daily Caseload',
        weekly: 'Weekly Caseload',
        monthly: 'Monthly Caseload',
      },
      items: {
        totalSessionsBooked: 'Total Sessions Booked',
        newClientsBooked: 'New clients booked',
        clientsReturning: 'Clients returning',
      }
    },
    availability: {
      availabilityCalendar: {
        title: 'Matching Availability',
        titleDetails: 'New clients can see and book these times',
        unavailableDate: 'Unavailable to new clients',
        availableHours: 'Available Hours',
        backToSchedule: 'Back to schedule',
      },
      editForm: {
        addTimeBlock: 'Add time block',
        removeAllAvailability: 'Remove all Availability',
        revertToDefault: 'Revert to default',
        revertToRecurring: 'Revert to recurring availability',
        startTime: 'Start Time',
        endTime: 'End Time',
        lessThenHourWarningTitle: 'Psychotherapy clients can’t book time blocks less than 1 hr.',
        buttons: {
          add: 'New Block',
          remove: 'Delete Block',
          copyToOtherDays: 'Copy to other days',
        },
      },
      availabilityTips: {
        buttonTitle: 'Availability Tips',
        title: 'Availability Tips to Attract Clients',
        tips: [
          'Clients prefer Tuesday, Wednesday, and Thursday sessions',
          'Most clients book between 10 AM and 6 PM ET',
          'Long blocks of time get more bookings. Try a 4-hour block instead of many 1-hour blocks',
          'Calculate your hours: How many new clients do you want each week? Multiply it by 1.5',
        ],
      }
    }
  },
  sessions: {
    done: 'Session completed',
    reviewNotes: 'Review Notes',
    connectionTroublesText: `If you have trouble connecting, call or text ${SUPPORT_NUMBER}`,
    successfullyCanceled: 'Session canceled',
    successfullyCanceledAll: 'All sessions canceled',
    cancelError: 'Error cancelling the session',
    cancelSession: 'Cancel Session',
    session: 'Session',
    start: 'Start',
    todaysSessions: 'Today’s Sessions',
    join: 'Join',
    submitSession: 'Submit Session',
    noSession: 'No Session',
    noPreviousSession: 'No previous session',
    nextSession: 'Next session',
    yourNextSessionIs: (formattedSessionDate: string) => `Your next session is ${formattedSessionDate}.`,
    microphone: 'Microphone',
    speaker: 'Speaker',
    camera: 'Camera',
    you: 'You',
    details: 'Details',
    cancelNoSessionOccurred: 'Cancel Session',
    endSession: 'End Session',
    rejoinSession: 'Rejoin Session',
    joinSession: 'Join Session',
    returnHome: 'Return Home',
    leftTheSession: 'You left the session',
    rejoin: 'rejoin',
    noSessionOccurred: 'No Session Occurred',
    nextSessions: 'Next Sessions',
    submittedBillings: (month: string) => `submitted${month ? ` ${month}` : ' '} billings`,
    readyToJoin: 'Ready to join?',
    unableStartSession: {
      title: 'UpLift is unable to start your session',
      descFirst:
        'It looks like your device isn’t currently supported for online sessions. Please double check you are connected to the internet and settings for the camera, microphone, and speaker are correct.',
      descSecond:
        'If you still experience issues—try joining your session from another browser, computer, or mobile device.',
    },
    allowDevices: {
      title: 'Allow UpLift to use your camera and microphone',
      line1: 'To have an UpLift call, you need to give your browser permission to access your webcam and microphone.',
      line2: 'View our help article on how to set that up for your browser.',
      readInstructions: 'Read instructions',
    },
    noSessionOccurredModal: {
      title: 'Mark the client as a no show',
      description:
        'Marking the client as a no show will remove this session from your schedule today. To continue, choose reschedule or cancel session.',
    },
    bookNewClients: {
      title: 'Book New Clients',
      tryTheseTips: 'Your schedule is open. Try these tips to match with more clients!',
      updateAvailability: 'Update Availability',
      updateProfile: 'Update Profile',
      availabilityTip: 'Make sure you have a variety of availability.',
      specialtiesTip: 'Providers with 5+ areas of specialty get more bookings.',
      availabilityDescription: "Your schedule is open. Tip: Offer a variety of availability to match with more clients. You can always update your availability when you've reached your ideal caseload.",
      specialtiesDescription: 'Your schedule is open. Tip: Did you know that providers with 5 or more clinical specialties get more bookings?',
      yourScheduleIsOpen: 'Your schedule is open',
      noUpcomingSessions: "No upcoming sessions. You'll receive an email when you match with a new client."
    },
    detailsModal: {
      when: 'When',
      where: 'Where',
      previousSession: 'Previous session',
      contactInformation: 'Contact Information',
      mobile: 'Mobile',
      email: 'Email',
      progressNote: 'Progress note',
      phoneWillBeAvailableAfterCreatesAccount: 'Available after client creates their account',
    },
    betterInPortraitModal: {
      switch: 'Switch to portrait mode for a better experience',
      gotIt: 'Got it',
    },
    networkConnectivityAlert: (role: string) => `You or your ${role === 'provider' ? 'patient' : 'provider'} has an unstable internet connection`,
    backgroundBlur: 'Background blur',
    connectionTips: {
      modalTitle: 'Tips for a good call',
      havingIssues: 'Having connection issues?',
      hasAccess: 'Make sure your browser has access to your camera, microphone, and speaker.',
      viewHelp: 'View our help article on how to set that up for your browser.',
      readInstructions: 'Read Instructions',
      strongInternet: 'Use strong internet. Move closer to a router then connect to Wi-Fi or go somewhere with strong 5G signal.',
      strongWifi: 'Use strong Wi-Fi. Move closer to your router if you can.',
      closeUnused: 'Close unused programs or restart your computer.',
      update: 'Update to the latest version of Chrome or Safari',
      stillHavingTrouble: 'Still having trouble?'
    }
  },
  noSessionsScheduledForToday: {
    noUpcomingSessions: 'No Upcoming Session',
    scheduleDetails1: 'Continue your mental health journey and schedule time with a therapist.',
    findATherapist: 'Find a therapist',
    scheduleDetails2: `or consult a matching specialist at ${HELP_EMAIL}`,
    emailYourProvider: 'Find time for you with someone who cares. Email your provider\nto schedule a new session together.'
  },
  unsubmittedSessions: {
    title: 'Unsubmitted Sessions',
    sessionsToSubmitOrCancel: (amount: number) => `You have <b>${amount}</b> sessions to submit or cancel.`
  },
  signupIncomplete: {
    details: "This session won't start until the client finishes setting up their account. Contact the client to remind them to complete their account information or to reschedule their session.",
    clientEmail: 'Client’s email',
  },
  unattendedSession: {
    title: 'Session expired',
    desc: `Telehealth sessions expire if you do not join the session. Email <strong>${HELP_EMAIL}/strong> to submit the session.`,
  },
  payments: {
    payments: 'Payments',
    insurance: 'Insurance',
    finance: 'Finance',
    details: 'Details',
    cardErrors: {
      card_declined: 'This card was declined. Add more money to your account or use another card.',
      incorrect_number: 'There was a problem saving your card. Check your card number then try again or use another card.',
      expired_card: 'There was a problem saving your card. Check your expiration date then try again or use another card.',
      incorrect_cvc: 'There was a problem saving your card. Check your CVC then try again or use another card.',
      generic: 'There was a problem saving your card. Check your information then try again or use another card.',
    }
  },
  patients: {
    name: 'Name',
    lastSession: 'Last Session',
    status: 'Status',
    nextSession: 'Next Session',
    location: 'Location'
  },
  supervisees: {
    emptyState: 'No supervisees yet',
    noMeetings: 'No supervision meetings yet',
    meetingMethods: {
      video: 'Video call',
      phone: 'Phone call',
      in_person: 'In person',
    },
  },
  requestRematch: {
    findANewTherapist: 'Find a new therapist'
  },
  clientProfile: {
    sections: {
      psychiatry: 'Psychiatry'
    },
    psychiatry: {
      noInfoYet: 'No psychiatry information yet',
      connect: 'connect to a Provider',
      visits: {
        visitSummary: 'Visit Summary',
        nextVisit: 'Next Visit',
        lastVisit: 'Last Visit',
        initialConsult: 'Initial Consult',
        followUp: 'Follow up',
        minutes: (num: number) => `${num} minutes`,
      },
      provider: {
        psychiatrist: 'Psychiatrist',
        work: 'work',
      }
    },
    allergies: {
      noAllergies: 'No known drug allergies'
    },
    medicalConditions: {
      noMedicalConditions: 'No known medical conditions'
    },
    assessments: {
      score: 'Score',
      navButtonBack: (patientName: string) => `Back to ${patientName}’s Chart`,
      assessmentDescriptionTitle: {
        phq9: 'Depression',
        gad7: 'Anxiety',
        PCL5: 'PTSD',
      },
      scores: {
        phq9: {
          answers: {
            notAtAll: 'Not at all',
            severalDays: 'Several days',
            moreThanHalf: 'More than half the days',
            nearlyEvery: 'Nearly every day',
          },
          scoreDescriptions: {
            not: 'Not depressed',
            mildly: 'Mildly depressed',
            moderately: 'Moderately depressed',
            moderatelyToSeverely: 'Moderately to severely depressed',
            severely: 'Severely depressed'
          },
        },
        gad7: {
          scoreDescriptions: {
            not: 'Not anxious',
            mildly: 'Mildly anxious',
            moderately: 'Moderately anxious',
            // here for a type fix
            moderatelyToSeverely: '',
            severely: 'Severely anxious',
          }
        },
      }
    }
  },
  patientIntakeForm: {
    submittedOn: 'Submitted on',
    atTime: 'at',
    backToChart: 'Back to chart'
  },
  providerCptRates: {
    viewRates: 'View Rates',
    title: 'Provider Payment Rates',
    search: 'Search CPT code or description...',
  },
  psychiatry: {
    connect: (name: string) => `Connect ${name} to a Provider`,
    doesNotHaveInsurance: (name: string) => `${name} does not have insurance. A psychiatric consultation will cost them $250.`,
    nextAvailable: 'Next available:',
    viewProfile: 'view profile',
    confirm: {
      confirm: (name: string) => `Confirm ${name}'s Consultation`,
      informationRequired: (lastName: string) => `Is there any information ${lastName} needs to know?`,
      book: 'Book consultation',
      with: 'with',
      unknownError: `Something went wrong, Please try again or contact <a href="mailto:${HELP_EMAIL}">${HELP_EMAIL}</a> for support.`,
      scheduled: 'Consultation scheduled',
      sentEmail: (name: string) => `We sent ${name} a confirmation email`,
      noCS: 'UpLift Behavioral Health providers do not issue prescriptions for controlled substances. Please inform your patient prior to scheduling them with the psychiatry provider.'
    }
  },
  schedule: {
    reviewYourSchedule: 'Review your schedule',
    configuration: {
      idealCaseload: {
        setYourRecurringAvailability: 'Set your Recurring Availability',
        whatIsYourIdealWeeklyCaseload: 'What is your ideal weekly caseload?',
        weCanSuggestHowManyHours: 'We can suggest how many hours you need to help you meet that goal.',
      },
      availability: {
        setYourRecurringAvailability: 'Set your Recurring Availability',
        whenYouWillBeRegularlyAvailable: 'When will you be regularly available?',
        availabilityDescription: 'When new clients join UpLift, your availability shows them 1-hour blocks that they can book with you. Without any availability—or without enough—new clients can’t find time to schedule new sessions with you.',
        tip: 'Long blocks of time get more bookings. Try a 4-hour block instead of scattering 1-hour blocks.',
        availableHours: (hours: number) => `<strong>${hours}</strong> available hours`,
        recommendAvailableHours: (hours: number) => `To meet your ideal weekly caseload goals, we recommend you have at least ${hours} hours of available hours.`,
        finishSetup: 'Finish setup',
      },
      calendarIntegration: {
        setYourRecurringAvailability: 'Set your Recurring Availability',
        doYouWantToConnect: 'Do you want to connect a Google Calendar with UpLift?',
        youWillBeAbleToSeeYourEvents: 'You’ll be able to see your Google Calendar events on UpLift and your matching availability will be automatically updated when there are conflicts. ',
      }
    },
    cancelAppointment: {
      whyCancel: 'Why are you canceling?',
      whyAreYouCanceling: 'Cancellation reason (Not shown to client)',
      fee: 'Charge client late cancel fee',
      feeWarning: (amount: string) => `This session occurs within 24 hours. If you cancel this session, you will be charged a ${amount} late cancel fee.`,
      feeWarningMedicaid: 'This session is in less than 24 hours, so this would be a late cancel. You might be unable to use UpLift after either 3 late cancels or missed sessions.',
      whatToCancel: 'What do you want to cancel?',
      allSessions: 'All sessions',
      addNote: 'Add a cancellation note for your client',
      sessionWith: (name: string) => `Cancel Session with ${name}`,
      allWith: (name: string) => `Cancel All Sessions with ${name}`,
      sessionAtWith: (date: string, time: string, name: string) => `Cancel ${date} at ${time} with ${name}`,
      cancel: (all: boolean) => `Cancel Session${all ? 's' : ''}`,
      reason: (reason: string) => `Reason: ${reason}`,
      why: "Let your provider know more about why you're canceling",
      keepAllSessions: 'Keep all sessions',
      cancelAllSessions: 'Cancel all sessions',
      keepSession: 'Keep session',
      cancelSession: 'Cancel session',
      feeText: {
        within24hrs: 'Clients can be charged for canceling less than 24 hours before the session',
        over24hrs: "Clients can't be charged for canceling at least 24 hours before the session",
        over7DaysOld: "Clients can't be charged cancellation fees for sessions older than 7 days",
        medicaid: "Clients using Medicaid as their primary insurance can't be charged fees. If a client misses or late cancels 3 sessions, you can request they be discharged.",
        isInitial: 'Client has not completed onboarding. There is no card on file to charge a late fee.',
      }
    },
    createAppointment: {
      newSession: 'Add session',
      searchExistingClients: 'Search for client',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email address',
      notes: 'Notes',
      patientInvitationAlert: 'Invited clients need to create an account and add billing information before their first session.',
      notesToClient: 'Add an optional note for the client',
      schedule: 'Schedule',
      recurring: 'Recurring',
      scheduleExistingPatient: 'Schedule existing client',
      inviteNewPatient: 'Invite new client',
      sessionAdded: 'Session added',
      sessionsAdded: 'Sessions added',
      invitationSent: (name?: string) => `Invitation sent to ${name ?? 'patient'}`,
      recurringOptions: {
        none: 'Does not repeat',
        weekly: 'Every week',
        biweekly: 'Every 2 weeks',
        monthly: 'Every month',
      },
      providerErrors: {
        unknown: {
          body: `There was a problem adding this session. Try again or contact <b>${HELP_EMAIL}</b>`,
        },
        clientAlreadyHasAccount: {
          body: 'Client already has an account. <b>Use Schedule existing client.</b>',
        },
        clientMatchedWithAnotherProvider: {
          body: `Client already has an account. Email <b>${HELP_EMAIL}</b> to schedule their session.`,
        },
        slotConflict: {
          body: 'This series conflicts with a different session in the future. Choose a different time and try again.',
        },
        slotConflictWithIcs: {
          body: "This slot is conflicting with provider's personal schedule. Choose a different time and try again.",
        },
        slotNoLongerAvailable: {
          body: 'This session overlaps with your next session. Update the session duration or start time to continue.',
        },
        slotIsNotAvailableForClient: {
          body: 'The client has a session with their therapist at this time. Choose a different day to continue.',
        },
        patientHasBookedAppointmentForCurrentDay: {
          body: 'The client has another UpLift appointment on this day. To ensure the appointment will be reimbursed by insurance, please select another day.',
        },
        patientHasBookedAppointmentWithAnotherProviderForCurrentDay: {
          body: 'The client has another UpLift appointment on this day. To ensure the appointment will be reimbursed by insurance, please select another day.',
        },
        recurringSessionDateConflictWithPatientSessionInTheFuture: {
          body: 'This schedules a recurring session on a day that the client has another UpLift session. To ensure the client is reimbursed by insurance, select another day.',
        },
        notAllowedInThePast: {
          body: "You can't create session in the past",
        },
        notAllowedSoFarInTheFuture: {
          body: "You can't create session so far in the future",
        },
      },
      referralErrors: {
        slotIsNotAvailableForClient: {
          body: 'The client has a session with their therapist at this time. Choose a different day to continue.',
        },
        slotNoLongerAvailable: {
          body: 'Time slot is not available. Please choose a different time slot.',
        },
      },
      errors: {
        slotConflict: {
          title: 'We found a conflict with this time slot',
          body: 'A future appointment is booked during this recurring time. Please choose a different time slot.',
        },
        slotConflictWithIcs: {
          title: 'We found a conflict with this time slot',
          body: "This slot is conflicting with provider's personal schedule. Please choose a different time slot.",
        },
        slotNoLongerAvailable: {
          title: 'This time is no longer available',
          body: 'Another client just booked this session. Please choose a different time slot.',
        },
        patientHasBookedAppointmentForCurrentDay: {
          body: 'You have another UpLift appointment on this day. To ensure the appointment will be reimbursed by insurance, please select another day.',
        },
        patientHasBookedAppointmentWithAnotherProviderForCurrentDay: {
          body: 'You have another UpLift appointment on this day. To ensure the appointment will be reimbursed by insurance, please select another day.',
        },
        recurringSessionDateConflictWithPatientSessionInTheFuture: {
          body: 'This schedules a recurring session on a day that you have another UpLift session. To ensure you are reimbursed by insurance, select another day.',
        },
        notAllowedInThePast: {
          body: "You can't create session in the past",
        },
        notAllowedSoFarInTheFuture: {
          body: "You can't create session so far in the future",
        },
      }
    }
  },
  paymentType: {
    cash: 'Cash',
    insurance: 'Insurance',
  },
  insuranceType: {
    primary: 'Primary',
    secondary: 'Secondary',
  },
  appointments: {
    type: {
      therapy: 'Psychotherapy',
      psychiatry: 'Psychiatry',
      psychiatricCare: 'Psychiatric Care',
      psychConsultation: 'Psych consultation',
      medicationManagement: 'Medication Management',
    },
    recurring: {
      weekly: 'Weekly',
      biweekly: 'Biweekly',
      monthly: 'Monthly',
      none: 'None',
    },
    visitLocation: {
      virtual: 'Virtual (Video and Audio)'
    },
    duration: {
      sixty: '60 minutes',
      sixtyShort: '60 min'
    },
    reminders: {
      completed_first_session: '<strong>Completed</strong> their first psychiatry session',
      scheduled_first_session: '<strong>Scheduled</strong> their first psychiatry session',
      cancelled_first_session: '<strong>Canceled</strong> their first psych session',
      did_not_show_up_to_first_session: '<strong>Did not show</strong> up to their first psychiatry session',
    },
  },
  finance: {
    title: 'Finance',
    payoutSubtitle: "We'll send your payout after AmeriHealth processes any claims.",
    summary: {
      calculationWarning: "One or more of your submitted sessions won’t automatically calculate—but don’t worry: We'll take care of this issue before you receive your payout."
    },
    csvDownloaded: 'Session history downloaded',
    csvTitle: (date: DateObject) => `Uplift_Finances_${date.format('DD_MM_YYYY')}.csv`,
    monthly: {
      calculationWarning: 'There was an issue calculating the payout of these submitted sessions. We will resolve the issue before the pay period ends.',
      cancelled: 'Cancellation',
      lateFee: 'Fee',
      payoutCouldNotBeCalculated: 'The payout could not be automatically calculated. We’ll update the amount before we process your next payout.',
      paymentInitiatedOn: (payoutDate: string) => `Your payment was initiated on ${payoutDate}.`,
      paymentInitiatedMayTake: 'It may take up to 10 business days for your bank to process',
      tbd: 'TBD',
      csvTitle: (date: DateObject) => `Uplift_Finance_${date.format('MMMM_YYYY')}.csv`,
      canceledWithLateFee: 'Canceled session with late fee',
      removedAndIssuedRefund: 'Removed late fee and issued refund to client'
    },
    paymentType: {
      cash: 'Cash Pay',
      insurance: 'Insurance'
    },
    summaryTable: {
      month: 'Month',
      submittedSessions: 'Submitted Sessions',
      cashPaySessions: 'Cash Pay Sessions',
      insuranceSessions: 'Insurance Sessions',
      cancellationFees: 'Cancellation Fees',
      payout: 'Payout',
      sessions: 'Sessions',
      fees: 'Fees',
      total: 'Total',
      billings: 'Submitted Billings',
    },
    monthTable: {
      submitted: 'Submitted',
      encounterDate: 'Encounter Date',
      client: 'Client',
      clientState: 'Client State',
      description: 'Description',
      payment: 'Payment',
      billings: 'Billings',
      submittedBillings: 'Submitted Billings'
    },
    noSessionsForThisPeriod: 'No sessions for this period'
  },
  onboarding: {
    footer: {
      tooltip:
        'I understand that any information that I voluntarily share as part ' +
        'of the provider selection process does not create a ' +
        'provider-client relationship between UpLift and me. Sharing my ' +
        'personal information, including information relating to my ' +
        'physical and/or mental health and any confirmed, suspected or ' +
        'potential conditions or diagnoses does not guarantee that I will ' +
        'receive services or other forms of treatment from UpLift. I ' +
        'realize that any information shared as part of the provider ' +
        'selection process is subject to UpLift’s Privacy Policy.',
      tooltipBold: 'In the event of a medical emergency, I understand that I should immediately call 911.'
    },
    connect: {
      helpText: `Email ${HELP_EMAIL} and one of our matching specialists will help you find a new provider.`,
      cantBook: (state: string) => `We can't book this session because your provider must be licensed in the state where you live. We have many quality providers licensed in ${state}—let us help you match with one.`,
      notLicensed: (providerName: string, state: string) => `${providerName} isn't licensed in ${state}`,
      copyEmail: 'Copy email',
      findATherapist: 'Find a therapist'
    },
    reason: {
      visitReasons: 'Visit Reasons',
      video: 'Video',
      videoNow: 'Video now, in person later',
      itDepends: 'It depends, I want flexibility',
      searchPlaceholder: 'Search concerns…',
      searchLabel: 'Search concerns',
      noSearchResults: 'No concerns match your search. Try a different term, or select "My reason isn\'t here" below.',
      nextDisabledWarning: 'Select at least one concern to continue.',
      categories: {
        moodAndEmotions: 'Mood and emotions',
        focusAndBehaviors: 'Focus and behaviors',
        traumaAndFears: 'Trauma and fears',
        relationshipsAndIdentity: 'Relationships and identity',
        lifeChangesAndHealth: 'Life changes and health',
        ongoingConditions: 'Specialized mental health support',
        other: 'Other concerns'
      },
      selectedCount: (count: number) => count === 1
        ? "1 selected. We'll match you with providers who treat this concern"
        : `${count} selected. We'll match you with providers who treat these concerns`,
      stimulantDisclaimer: 'UpLift providers do not prescribe controlled substances, including stimulants like Adderall. Our providers offer therapy and non-stimulant medication options where appropriate.',
      benzodiazepineDisclaimer: 'UpLift providers do not prescribe controlled substances, including benzodiazepines like Xanax. Our providers offer therapy and non-controlled medication options where appropriate.',
      combinedDisclaimer: 'UpLift providers do not prescribe controlled substances, including stimulants like Adderall and benzodiazepines like Xanax. Our providers offer therapy and non-stimulant and non-controlled medication options where appropriate.',
    },
    ages: {
      ranges: {
        children: '<12',
        adolescents_or_teens: '13–17',
        young_adults: '18–25',
        adults: '26–64',
        elderly: '65+',
      }
    },
    typeOfTherapy: {
      types: {
        individual: 'Individual therapy',
        couples: 'Couples therapy',
        group: 'Group therapy',
        families: 'Family therapy',
      }
    },
    insurance: {
      sessionPay: 'I’ll pay for services myself',
      selfPay: 'Self Pay',
      providerInsuranceHint: "Why don't I see my insurance?"
    },
    account: {
      firstName: 'First Name',
      middleName: 'Middle Name (optional)',
      lastName: 'Last Name',
      preferredName: 'Preferred First Name (optional)',
      addPreferredName: 'Add Preferred First Name (optional)',
      email: 'Email Address',
      password: 'Password',
      sex: 'Sex',
      binary: 'We know gender isn’t binary, but insurance carriers require a sex',
      pronouns: 'Pronouns (optional)',
      dateOfBirth: 'Date of Birth'
    },
    welcome: {
      title: 'Welcome to UpLift!',
      getStarted: 'Let’s get started',
      getStartedButton: 'Get Started',
      upcomingSessions: 'You’ll find your upcoming sessions for the day here once you start booking with clients.'
    },
    general: {
      personal: {
        pronouns: 'Pronouns',
        pronounsExample: 'He/him, She/her, They/Them, Ze/hir, etc.'
      }
    },
    practice: {
      licenseDetails: {
        mentalHealthRole: 'Mental Health Role',
        number: 'License Number',
        state: 'License State',
        expiration: 'License Expiration'
      }
    },
    errors: {
      chooseYourAgeGroup: 'Choose your age group',
    }
  },
  matching: {
    recurring: {
      weekly: 'weekly',
      biweekly: 'every 2 weeks',
      monthly: 'every 4 weeks',
    },
    providers: {
      noDateAvailable: 'No date available',
      forYou: 'for you',
      hotline: 'If you or any other person are in a life threatening situation, please call 1-800-273-8255 or 911.',
      specialties: 'Specialties',
      nextAvailable: 'Next available'
    },
    selectedProvider: {
      rebookAlertTitle: 'The session time you selected is no longer available. Select another time to continue booking this provider.',
      rebookAlertContent: `If you have any questions about scheduling sessions, email us at <a href="mailto:${HELP_EMAIL}">${HELP_EMAIL}</a>.`,
      scheduleSession: 'Schedule Session',
      bookConsultation: 'Book Consultation',
      bookYourWeeklySession: 'Book your weekly session',
      bookYourSession: 'Book your session',
      limitAlert: (duration: string) => `Appointments cannot be booked more than ${duration} in advance`,
      skipOrRescheduleAsNeeded: 'Sessions will always be on your schedule—you can skip or reschedule as needed',
      meetWithProvider: (providerName: string) => `Meet with ${providerName}`,
      bookWeeklySession: 'Book Weekly Session',
      confirmWeeklySession: 'Confirm Weekly Session',
      billedUntilAfterFirstSession: "You won't be billed until after your first session",
      confirmConsultation: 'Confirm Consultation',
      confirm: 'Confirm'
    },
    insurance: {
      memberId: 'Member ID',
      groupNumber: 'Group number (optional)',
      primarySub: 'Primary subscriber',
      streetAddress: 'Street Address',
      streetAddress2: 'Apartment, Suite, Floor, etc. (optional)',
      addStreetAddress2: 'Add apartment, suite, floor, etc. (Optional)',
      addStreetAddress2SmallBreakpoint: 'Add apt, ste, fl, etc. (Optional)',
      uploadFrontSide: 'Front side',
      uploadBackSide: 'Back side',
      noAvailableInsurances: 'No available insurances',
      insuranceQuestions:
        'Please reach out to your insurance plan or HSA/FSA provider if you have questions about coverage or costs.',
      dateOfBirth: 'Date of Birth',
      dateOfBirthTip: 'MM/DD/YYYY',
      arePrimarySubscriber: 'Are you the primary subscriber?',
      insuranceProvider: 'Insurance Provider',
      insuranceCard: 'Insurance Card',
      primarySubAddress: 'Primary subscriber address',
      primarySubscriberTooltip1: 'If you were added to your insurance plan by someone else, you might not be the primary subscriber.',
      primarySubscriberTooltip2: 'The primary subscriber is the main person on the insurance plan. Also called the "policy holder," this person owns the plan because they might pay the premiums for it or they get insurance through work.',
      modal: {
        title: "Why don't I see my insurance?",
        line1: (providerName: string) => `This list only shows insurances your provider accepts. If you don't see your insurance listed, ${providerName} doesn't accept it.`,
        line2: (providerName: string) => `If you still want to do sessions with ${providerName}, select Skip Insurance to pay the reduced cash pay price ($125—$145). For help matching with a different provider who accepts your insurance, email us at ${HELP_EMAIL}.`,
        gotIt: 'Got it',
      }
    },
    payment: {
      nameOnCard: 'Name on Card',
      cardNumber: 'Card Number',
      expDate: 'Expiration Date',
      cvc: 'CVC',
      billingAddress: 'Billing Address',
      autoPayments: 'By continuing you agree to auto-payments.',
      contactInfo: 'Contact',
      mobilePhone: 'Mobile Phone',
      homePhone: 'Home (optional)',
      addHomePhone: 'Add home phone number (optional)',
      accept: 'I have read and accept the following:',
      confirmState: (state: string) => `I will be in ${state} during all my virtual visits`,
      treatmentFormConsent: 'Consent to Treatment Form',
      privacy: 'Notice of Privacy Practices',
      finResponsibility: 'Patient Financial Responsibility Agreement',
      medicationAcknowledgment: 'I understand that UpLift providers do not prescribe controlled substances such as Adderall or Xanax.',
      cancellationFeeNotice: (fee: string) => `If you cancel within 24 hours of your appointment, you'll be charged a cancellation fee of ${fee}.`,
      paymentAuthorization: "You won't be charged now. By selecting Next, you authorize UpLift to charge this card after your session.",
      cashPrice: 'Reduced cash price',
      accepts: 'Accepts',
      sessionPrice: (price: string) => `${price} a session`,
      cashPayDesc: 'Please note that by selecting the cash pay option you are taking full responsibility for the costs associated with this session. Please reach out to your insurance plan or HSA/FSA provider if you have any questions about reimbursement options.'
    },
    profilePhoto: {
      choosePhoto: 'Choose photo',
      chooseNewPhoto: 'Choose new photo',
      photoDetailsFormat: 'jpg or png less than 10 MB',
      photoDetailsSize: 'minimum width 250px'
    },
    book: {
      reachOut: 'Please reach out to your insurance plan or HSA/FSA provider if you have questions about coverage or costs.',
    },
    firstSessionNew: {
      letsStart: 'Let’s start by verifying your contact information',
      verifyPhone: 'Verify your phone number'
    },
    firstSession: {
      goToDashbaord: 'go to your dashboard'
    }
  },
  profile: {
    title: 'My Profile',
    location: 'Location',
    congratsComplete: {
      title: 'You’re almost ready',
      details: 'Now that you’ve finished your profile, it’s time to learn how to use UpLift’s tools to grow your practice.',
      startYourTraining: 'Start your training'
    }
  },
  insurance: {
    memberId: 'Member ID',
    groupNumber: 'Group Number',
    primaryInsurance: 'Primary Insurance',
    secondaryInsurance: 'Secondary Insurance',
    insuranceCard: 'Insurance Card',
    provider: 'Provider',
    dontSeeInsurance: "Don't see your insurance?",
    moreCarriersComingSoon: 'More carriers are coming soon. To start therapy now, choose "none" and pay a reduced cash rate.',
    moreCarriersComingSoonExcludeCashPay: 'More carriers are coming soon. Hit "cancel" below to continue paying a reduced cash rate.'
  },
  states: {
    other: 'Other',
    dontSeeState: "Don't see your state?",
    moreStatesComingSoon: 'More states are coming soon. Stay in touch by selecting this option.',
  },
  providerSettings: {
    basics: 'Basics',
    name: 'Name',
    legal: 'Legal',
    legalName: 'Legal Name',
    preferred: 'Preferred first name',
    preferredNameDuplicateLastName: 'Only your first name is needed — your last name is added automatically.',
    dateOfBirth: 'Date of birth',
    editDateOfBirth: 'Edit Date of Birth',
    sex: 'Sex',
    prefix: 'Prefix',
    suffix: 'Suffix',
    contactInformation: 'Contact Information',
    homeAddress: 'Home address',
    phone: 'Phone',
    mobile: 'Mobile',
    work: 'Work',
    home: 'Home',
    accountEmail: 'Account email',
    email: 'Email',
    needToChangeEmail: `Need to change? Contact ${HELP_EMAIL}`,
    unverified: 'Unverified—keep your account secure and',
    verify: 'verify your mobile number',
    login: 'Login',
    password: 'Password',
    changePassword: 'Change Password',
    change: 'Change',
    homeAddressRequired: {
      address1: 'Enter a street address for your home',
      city: 'Enter your home city',
      state: 'Enter your home state',
      zip: 'Enter your home ZIP code',
    },
    sessionOption: (locationName: string) => `What type of session do you want to hold at ${locationName}?`,
    onboarding: {
      introductoryVideo: 'We’re excited to have you join UpLift. Watch this quick introductory video so you know what to expect from the process:',
      gettingStarted: 'Getting Started',
      progressSummary: 'Progress Summary',
      practiceStates: 'Practice States',
      credentialing: 'Credentialing',
      haveAQuestion: 'Have a question?',
      helpCenter: 'help center',
      matchingProfile: 'Matching Profile',
      matchingAvailability: 'Matching Availability',
      createProfile: 'Create Profile',
      addHours: 'Add Hours',
      selectStates: 'Select States',
      didYouKnowTitle: 'Did you know...',
      didYouKnowMessages: [
        '40% of people don’t seek out mental healthcare because they can’t afford it and with your help, we are going to make it easier for them to get the help they need.',
      ],
      allToDosDone: 'All To Do’s done. There’s nothing for you to do right now',
      checkEmailAboutGoLiveDate: 'Check your email for more details about your go-live date.',
      allSet: 'All set—you’re going',
      liveOnUpliftSoon: 'live on UpLift soon',
      youAreLive: 'You’re live',
      getStartedMessage: 'Matching with new clients, invite current ones — and grow your own practice. Get started from your dashboard.',
      goToDashboard: 'Go To Dashboard',
      continue: 'Continue',
      providerAvailabilityData: (idealCaseload: number, totalHours: number) => `<b>${idealCaseload}</b> ideal caseload, <b>${totalHours}</b> hours added`,
      experienceIsBetterOnDesktop: 'Our onboarding experience\nis better on the desktop.',
      taskReminder: 'Reminder—the faster you complete these tasks, the faster you can go live.',
      finishAfterCredentialingApproved: 'Great job getting through your list—you can finish your last to do item once you’re approved by credentialing.'
    }
  },
  accountSettings: {
    isOptional: (field: string) => `${field} (Optional)`,
    firstName: 'First Name',
    lastName: 'Last Name',
    dateOfBirth: 'Date of Birth',
    dateOfBirthTip: 'MM/DD/YYYY',
    address: 'Address',
    changesCouldNotBeSaved: `Changes could not be saved. Try again or contact ${HELP_EMAIL}`,
    changesUpdatedSuccessfully: 'Changes updated successfully',
    profile: {
      aboutYou: 'About You',
      name: 'Name',
      legal: 'Legal',
      preferred: 'Preferred',
      preferredName: 'Preferred first name',
      sex: 'Sex',
      binary: 'We know gender isn’t binary, but insurance carriers require a sex',
      pronouns: 'Pronouns',
      contactInformation: 'Contact Information',
      homeAddress: 'Home Address',
      phone: 'Phone',
      mobile: 'Mobile',
      home: 'Home',
      email: 'Email',
      emailAddress: 'Email Address',
      changeEmail: `Need to change? Contact ${HELP_EMAIL}`,
      emergencyContact: 'Emergency Contact',
      noEmergencyContact: 'No Emergency Contact',
      whoShouldWeContact: 'In an emergency, who should we contact?',
      relationship: 'Relationship',
      fullName: 'Full Name',
      relationshipToYou: 'Relationship to You',
      mobilePhone: 'Mobile Phone',
      addMiddle: 'Add middle name',
      middleName: 'Middle Name'
    },
    billing: {
      paymentMethod: 'Payment Method',
      afterSessionCharge: "You won’t be billed until after your session. If you're paying with insurance, please allow 7 to 21 business days for processing.",
      paymentCard: 'Payment Card',
      cardholderName: 'Cardholder Name',
      cardNumber: 'Card Number',
      cvc: 'CVC',
      cardEndingIn: (card: string, last4: string) => `${card} ending in ${last4}`,
      expirationDate: 'Expiration Date',
      expirtationDateTip: 'MM/YY',
      expiresSoon: 'Expires Soon',
      expired: 'Expired',
      billingAddress: 'Billing Address',
      outOfPocketQuestions: 'Questions about out-of-pocket costs before your next session?',
      update: 'Update',
      updateAndPay: 'Update and Pay',
      outstandingBalance: 'Outstanding Balance',
      editPaymentMethod: 'Edit Payment Method',
      addPaymentMethod: 'Add Payment Method',
      autoPayOnNoPaymentDue: 'Autopay active: No payment due',
      noPaymentDue: 'No payment due right now.',
      lastPaymentReceived: (date: string) => `Last payment received ${date}.`,
      balancePaid: 'Balance paid',
      paidOne: 'Paid 1 session',
      paidMultiple: (count: number) => `Paid ${count} sessions`,
      reactivatedAndPaid: 'Account reactivated and balance paid',
      autopay: 'Autopay',
      turnOff: 'Turn off',
      turnOn: 'Turn on',
      turnOffAutopay: 'Turn Off Autopay',
      autopayIsOn: 'Autopay is on',
      autopayIsOff: 'Autopay is off',
      turnOnAutopay: 'Turn on autopay',
      turnOnAndPay: 'Turn on and pay',
      payBalanceAndTurnOnAutopay: 'Pay Balance and Turn On Autopay',
      autopayUpdateFailed: 'Autopay update failed. Try again or contact support',
      lateFeesWillContinueToBeCharged: 'Late fees will still be charged automatically. If you miss payment for 3 services, you could lose access to your account.',
      reviewAndPay: 'Review and Pay',
      editAndPay: 'Edit and Pay Balance',
      payBalance: 'Pay Balance',
      pay: 'Pay',
      expires: 'Expires',
      effortlessPaymentsWithAutopay: 'Effortless payments with Autopay',
      payBillsWithoutTheHassle: 'Pay bills without the hassle of logging in and remembering due dates.',
      cardWasDeclined: 'This card was declined. Add more money to your account or use another card.',
      twoUnpaidServices: 'You have 2 unpaid services. Your account will be deactivated after 3 unpaid services.',
      threeUnpaidServices: 'You have 3 or more unpaid services.',
      payOffYourBalance: 'Pay off your balance to avoid account deactivation.',
      declined: 'Declined',
      cardIsDeclined: 'Your card is declined',
      limitedAccount: 'Limited Account',
      deactivatedAccount: 'Your account has been deactivated, and access is limited. Clear your outstanding balance to reactivate your account.',
      updateYourPaymentMethod2: 'Update your payment method to pay off your balance.',
      updateYourPaymentMethod3: 'Avoid account deactivation by updating your payment method and paying off your outstanding balance.',
      cardIsExpiredTitle: 'Your card is expired',
      cardIsExpired: 'Your card is expired. Update your payment method before your next session.',
      afterSessionChargeDescription: "Your card won't be charged until after your session. If you're paying with insurance, please allow 7 to 21 business days for processing.",
      autopayEnsuresPeaceOfMind: 'Autopay saves time, prevents account deactivation, and ensures consistent payments—providing convenience and peace of mind.',
      restrictedAccessMessage: 'Pay off your balance to regain full access to your account',
      accessIsRestrictedTooltip: {
        line1: 'Access restricted. Pay off your balance to',
        line2: 'restore access.',
      },
      accessIsRestrictedToast: 'Access restricted. Pay off your balance to restore access.',
      restrictedAccess: 'Restricted Access',
      viewInsurance: 'View Insurance'
    },
    emailSettings: {
      toUpdateEmail: (role: string ) => `To update the account email, verify at least three pieces of this ${role}’s personal information`,
      selectVerified: 'Select Verified Personal Information',
      selectThree: 'select at least 3',
      changingEmail: (role: string) => `Changing the account email will update this ${role}’s login information.`
    },
    insurance: {
      activeInsurance: 'Active Insurance',
      activeNonPartnerInsurance: 'Active Non-Partner Insurance',
      archivedInsurance: 'Archived Insurance',
      primaryInsurance: 'Primary Insurance',
      secondaryInsurance: 'Secondary Insurance',
      primaryPayer: 'Primary Payer',
      secondaryPayer: 'Secondary Payer',
      yourInsurance: 'Your Insurance',
      noInsurance: 'No Insurance',
      insurance: 'Insurance',
      insuranceProvider: 'Insurance Provider',
      network: 'Network',
      memberId: 'Member ID',
      groupNumber: 'Group Number',
      insuranceAddedBy: (addedBy: string, addedOn: string) => `Insurance added by ${addedBy} on ${addedOn}`,
      planName: 'Plan Name',
      insuranceCode: 'Insurance Code',
      address: 'Address',
      benefits: 'Benefits',
      lastUpdate: 'Last Update',
      groupNumberOptional: 'Group Number (optional)',
      frontSide: 'Upload New Front Side',
      backSide: 'Upload New Back Side',
      primarySubscriber: 'Primary Subscriber',
      dateOfBirth: 'Date of Birth',
      primarySubscriberDetails: 'Primary Subscriber Details',
      primarySubscriberAddress: 'Primary Subscriber Address',
      userIsPrimarySubscriber: 'Are you the primary subscriber?',
      adminUserIsPrimarySubscriber: (patientName: string) => `Is ${patientName} the primary subscriber?`,
      isPrimarySubscriberTooltip: 'If you were added to your insurance plan by someone else, you might not be the primary subscriber.<br><br>The primary subscriber is the main person on the insurance plan. Also called the "policy holder," this person owns the plan because they might pay the premiums for it or they get insurance through work.',
      viewInsuranceCard: 'View Insurance Card',
      adminUnsupportedPrimaryInsurance: (patientName: string, providerName: string) => `Saving this insurance will change ${patientName}’s costs to ${providerName}’s cash pay rate. When you save this insurance to their account, we'll email them with their payment options.`,
      adminCareTeamUnsupportedInsuranceBody: (patientName: string) => `Saving this insurance will change ${patientName}’s cash pay rates. When you save this insurance to their account, we'll email them with their payment options.`,
      adminUnsupportedSecondaryInsurance: (patientName: string) => `Saving this insurance may change ${patientName}’s expected out-of-pocket cost. When you save this insurance to their account, we’ll email them with their payment options.`,
      unsupportedPrimaryInsurance: (cashPayAmount: string) => ({
        0: `You can pay for services yourself (${cashPayAmount}), contact your new insurance to `,
        petitionForContinuity: 'petition for continuity of care',
        1: ', or ',
        matchWithNewTherapist: 'match with a new therapist',
        2: `. Email ${HELP_EMAIL} if you have any questions.`
      }),
      unsupportedSecondaryInsurance: `Email ${HELP_EMAIL} if you have any questions.`,
      adminProviderUnsupportedInsurance: (providerName: string, insuranceName: string) => `${providerName} does not accept ${insuranceName}.`,
      providerUnsupportedInsurance: (providerName: string, insuranceName: string) => `Your provider ${providerName} does not accept ${insuranceName}.`,
      adminCareTeamUnsupportedInsuranceTitle: (patientName: string, insuranceName: string) => `${patientName}’s care team does not accept ${insuranceName}.`,
      careTeamUnsupportedInsurance: (insuranceName: string) => `Your care team does not accept ${insuranceName}.`,
      editInsurance: 'Edit Insurance',
      editPrimary: 'Edit Primary',
      deleteInsurance: 'Delete Insurance',
      deleteAndReplaceInsurance: 'Delete and Replace Insurance',
      ifDeleteInsurance: (amount: string) => `If you remove your insurance, you will be charged the ${amount} cash pay price.`,
      adminIfDeleteInsurance: (patient: string) => `Removing this insurance will charge ${patient} the cash pay rate.`,
      addNewInsurance: 'Add New Insurance',
      useInsurance: (insuranceName: string) => `Use ${insuranceName}`,
      removingMightChangeCost: 'Removing this insurance might change the cost of your sessions.',
      adminRemovingMightChangeCost: (patient: string) => `Removing this insurance might change the cost of ${patient}’s sessions.`,
      addressVerifyAlert: 'Confirm your address is correct before continuing.',
      adminAddressVerifyAlert: 'If the client is using insurance, this address may delay our ability to confirm their benefits.',
      addressFormattingSuggestion: 'Incorrect address formatting may increase the time it takes to verify your insurance.',
      useForPrimaryInsurance: 'What would you like to use for your primary insurance?',
      adminSelectReason: 'Select a reason for deletion then a new primary insurance.',
      emailForUpdatedCost: `Email us for your updated cost: ${HELP_EMAIL}`,
      insuranceCouldNotBeUpdated: `There was a problem updating your insurance. Try again or contact ${HELP_EMAIL}`,
      insuranceCouldNotBeAdded: `There was a problem adding your insurance. Try again or contact ${HELP_EMAIL}`,
      insuranceCouldNotBeDeleted: `There was a problem deleting your insurance. Try again or contact ${HELP_EMAIL}`,
      insuranceCardDropRejected: 'Image must be less than 10MB. Try reducing your image’s file size or choose another image to upload.',
      insuranceUpdated: 'Insurance updated',
      changesSaved: 'Changes saved',
      insuranceAdded: 'Insurance added',
      insuranceDeleted: 'Insurance deleted',
      isPrimaryInsurance: (insuranceName: string) => `Is ${insuranceName} your primary insurance?`,
      adminIsPrimaryInsurance: (insuranceName: string, patientName: string) => `Is ${insuranceName} ${patientName}’s primary insurance?`,
      selectReason: 'Select a reason for deletion',
      copay: 'Copay',
      coinsurance: 'Coinsurance',
      remainingDeductible: 'Remaining deductible',
      totalDeductible: 'Total deductible',
    },
    addInsurance: {
      addInsurance: 'Add Insurance',
      onlyInsurancesAcceptedByCareTeam: 'Only insurance accepted by your Care Team can be added.',
      adminOnlyInsurancesAccepted: (patientFirstName: string) => `${patientFirstName} hasn't added any insurance.`,
      insuranceDetails: 'Insurance Details',
      insuranceCard: 'Insurance Card',
      haveAnotherInsurance: 'Have Another Insurance?'
    },
  },
  settings: {
    address: {
      workPhone: 'Work Phone'
    }
  },
  serverError: 'Oops, something went wrong, please refresh the page!',
  updatePassword: {
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    errors: {
      currentRequired: 'Verify your current password',
      doNotMatch: 'Passwords do not match',
      confirmEmpty: 'Confirm your new password',
      pleaseCorrect: 'Please correct the errors below, and try again'
    }
  },
  editGeneralInfo: {
    city: 'City',
    state: 'State',
    zipCode: 'ZIP Code'
  },
  credentialDetailsModal: {
    editRosterDetails: 'Edit status'
  },
  phoneValidationModal: {
    verifyYourNumber: 'Verify your mobile number',
    verifyYourMobileNumberToReceiveTextReminders: 'Verify your mobile number to receive text reminders',
    enterCodeTextedTo: 'Enter the 4-digit code texted to',
    editMobileNumber: 'Edit Mobile Number',
    yourCodeWillExpire: 'Your code will expire in 10 minutes',
    sendNewCode: 'Send New Code',
    newCodeSent: 'New code sent',
    verifyMobileNumber: 'Verify Mobile Number',
    verifyCode: 'Verify code',
    textRemindersOn: 'Text reminders ON',
    errors: {
      'Invalid OTP': 'Incorrect code. Try again or send a new code.',
      'Contact already verified': 'Contact already verified',
    },
    mobileNumberVerified: 'Mobile number verified',
  },
  support: {
    pageTitle: 'Help Center',
    pageSubtitle: 'Find the guides and references you need. Still stuck? Reach out at the bottom of the page.',
    sendMessage: 'Send us a message',
    selectTopic: 'Select a Topic:',
    moreDetails: 'Can you give us more details?',
    placeholder: 'Add any information we can use to help you',
    submitSuccess: {
      title: 'Thank you for your message',
      subtitle: 'We’re looking forward to helping you! Our team will be in touch as soon as we can.',
    },
    submitFail: {
      desc: `An issue occurred while creating a support request. Please give us a call at ${SUPPORT_NUMBER}.`,
    },
    validation: {
      selectTopic: 'You need to select a topic',
      moreDetails: 'Please provide more details',
    },
    // used by uplift-care ContactSupportContainer — do not remove
    giveCall: 'Give us a call',
    supportNumber: SUPPORT_NUMBER,
    supportAvailability: 'We’re available Monday to Friday,<br />9 a.m.–4:30 p.m. ET.',
    footerPatient: 'If you or any other person are in a life threatening situation, please call 911 or the <a href="https://suicidepreventionlifeline.org/chat/" target="_blank">National Suicide Prevention Lifeline</a>',
    email: 'Email anytime',
    supportEmail: HELP_EMAIL,
    supportEmailOrg: HIGHER_ED_EMAIL,
    // Help Center redesign
    helpfulLinksHeading: 'Helpful links',
    browseHelpCenter: 'Browse the Help Center',
    goToHelpCenter: 'Go to Help Center',
    stillNeedHelp: 'Still need help?',
    submitRequestTitle: 'Submit a request',
    submitRequestDesc: 'Open a ticket and the team will follow up by email.',
    callUsTitle: 'Call us',
    callUsAvailability: 'Mon to Fri, 9 a.m.–4:30 p.m. ET.',
    emailUsTitle: 'Email us',
    emailUsReplyTime: 'We reply within one business day.',
  },
  rematching: {
    notMatchedTherapist: {
      youAreNotMatched: "You're currently not matched\nwith a therapist",
      findTherapist: 'Find a therapist',
      whyAmINotMatched: 'Why am I not matched',
      whyAmINotMatchedReasons: [
        'Unmatching can happen for a number of reasons. You may have chosen to stop seeing your previous therapist. Your therapist may have stopped practicing or left UpLift.',
        `We understand that sudden, unexpected changes in providers can be confusing and difficult. If you’d like to learn more about your situation, you can message our help team at <a href="mailto:${HELP_EMAIL}">${HELP_EMAIL}</a>.`,
      ]
    },
    rematchReasons: {
      differentSpecialty: 'I need a therapist with a different specialty',
      didNotConnectWithMyCurrentTherapist: 'I did not connect with my current therapist',
      therapistRecommendRematch: 'My therapist recommended I rematch',
      notHappyOfCare: "I'm not happy with the level of care I'm receiving ",
      movedAndTherapistNotSupport: "I moved, and my therapist isn't licensed in my state",
      other: 'Other',
    },
    providers: {
      found: (numProviders: number) => `We found ${numProviders} ${numProviders === 1 ? 'therapist' : 'therapists'}`,
      forYou: 'for you',
      noChange: "Decided you don't want to make a change?",
      return: 'Return to your dashboard',
    }
  },
  forms: {
    errors: {
      not_found: {
        title: 'Action item not found',
        subtitle: 'Try to access the item directly from your dashboard',
      },
      forbidden: {
        title: 'You do not have permission to complete this form',
        subtitle: 'Log in and access the item directly from your dashboard',
      },
      complete: {
        title: 'Action item completed',
        subtitle: "You've already finished this task—great work!",
      },
      expired: {
        title: 'This action item has expired',
        subtitle: "We'll send you an email when your next action item is ready. Try to complete the task as soon as you receive the notification.",
      },
      inactive: {
        title: "The form you're loading is not active.",
      },
      invalid_user: {
        title: 'This url is not yours.',
      },
      element_not_found: {
        title: 'Unable to find specified element',
      },
    },
    returnToDashboard: 'Return to dashboard',
    logIn: 'Log in',
  },
  findPsychiatrist: {
    title: 'Please select a time for your consultation',
    viewBy: 'View by',
    allProviders: 'All providers',
    emptyStateAllProviders: "We couldn't find time with our providers in the next month.",
    emptyStateLine2: `Your mental health is our priority. Let us help you schedule an appointment—email <a href="mailto:${HELP_EMAIL}">${HELP_EMAIL}</a>`,
    viewByTitle: 'Providers',
    timeBased: 'Time based on first available provider',
    confirmConsultation: 'Confirm Consultation',
    billed: "You won't be billed until after your session",
    confirm: 'Confirm',
    cancel: 'Cancel',
    tooFar: 'Appointments cannot be booked more than 1 month in advance',
    noCSWarning: 'UpLift Behavioral Health providers do not issue prescriptions for substances controlled by any federal or state agency or other drugs that may be harmful because of their potential for abuse. UpLift Behavioral Health reserves the right to limit the types of medications that may be prescribed by its doctors and nurse practitioners. All prescriptions furnished by UpLift Behavioral Health care providers are issued in accordance with applicable federal and state law as well as UpLift Behavioral Health policy.'
  },
  resources: {
    user: {
      role: {
        patient: 'Patient',
        provider: 'Provider',
        superadmin: 'Admin',
        external_admin: 'Admin',
      }
    },
    contacts: {
      labelByName: {
        phone: 'phone',
        work: 'work',
        cell: 'mobile',
        home: 'home',
        email: 'email',
      }
    },
    providerActivation: {
      statusLabel: {
        pending_invite: 'Awaiting Verifiable',
        invite_sent: 'Invited',
        awaiting_credentialing_info: 'Awaiting Credentialing Info',
        onboarding: 'Onboarding',
        ready_to_go: 'Ready to go',
        live: 'Live',
      },
      labelTooltips: {
        pending_invite: 'Providers will be invited automatically when Verifiable syncs their CAQH and NPI',
        invite_sent: 'Uplift sent the provider an invite',
        onboarding: 'Onboarding incomplete-open the provider’s profile to see what’s left',
        awaiting_credentialing_info: 'Providers must submit their credentialing username and password. Once verified, they will be moved to onboarding.',
        ready_to_go: 'The provider finished onboarding and is ready to go live'
      },
      events: {
        provider_added: (addedBy: string) => `Provider Added by ${addedBy}`,
        sign_on_bonus_added: 'Sign on bonus added',
        invite_sent: 'Provider Invite Sent',
        reminder_sent: 'Provider Reminder Sent',
        onboarding_initial: 'Provider Account Created',
        caqh_info_submitted: 'CAQH Information Submitted',
        introduction_video_completed: 'Introduction video completed',
        training_completed: 'Training completed',
        training_approved: (by: string) => `Training approved by ${by}`,
        training_submitted: 'Training submitted',
        training_started: 'Training started',
        matching_profile_submitted: 'Matching profile submitted',
        matching_profile_approved: (by: string) => `Matching profile approved by ${by}`,
        matching_availability_submitted: 'Availability submitted',
        matching_availability_approved: (by: string) => `Availability approved by ${by}`,
        credentialing_approved: (approvedBy: string) => `Credentialing Committee approved by ${approvedBy}`,
        insurance_added: (insuranceName: string, addedBy: string) => `${insuranceName} added by ${addedBy}`,
        insurance_removed: (insuranceName: string, removedBy: string) => `${insuranceName} removed by ${removedBy}`,
        license_added: (licenseType: string, addedBy: string) => `${licenseType} added by ${addedBy}`,
        license_removed: (licenseType: string, removedBy: string) => `${licenseType} removed by ${removedBy}`,
        live: (activatedBy: string) => `Pushed live by ${activatedBy}`,
        deactivated: (deactivatedBy: string) => `Deactivated by ${deactivatedBy}`,
        terminated: (terminatedBy: string) => `Deactivated by ${terminatedBy}`,
        ready_to_go: 'Ready to go',
        checkr_invitation_completed: 'Checkr form completed',
        checkr_report_completed: 'Checkr report completed',
        checkr_report_reviewed: 'Checkr report reviewed',
        referral_submitted: (submittedBy: string) => `Referral submitted for ${submittedBy} `,
      },
      requiredTrainings: {
        syncStatusLabel: 'Trainings synced from Relias',
        pending: 'pending',
        markCompleteButton: 'Mark complete',
        markCompleteConfirmTitle: 'Mark training as completed?',
        markCompleteConfirmDescription: (trainingLabel: string) => `Mark "${trainingLabel}" as completed? This overrides the Relias sync for this provider until it next reports a status.`,
        markCompleteConfirmButton: 'Continue',
      },
    },
    providerSignOnBonus: {
      tiers: {
        tier_1: 'Tier 1',
        tier_2: 'Tier 2',
      }
    },
    provider: {
      roles: {
        therapist: 'Therapist',
        psychiatrist: 'Psychiatrist',
      },
      types: {
        therapist: 'Licensed Therapist',
        psychiatrist: 'Psychiatrist',
        psychologist: 'Licensed Psychologist',
        pmhnp: 'Psychiatric-Mental Health Nurse Practitioner',
        psychiatricProvider: 'Psychiatric Provider'
      },
      prescriberTypes: {
        mddo: 'MD / DO',
        pmhnparpn: 'PMHNP',
      },
    },
    appointment: {
      recurring: {
        weekly: 'Weekly',
        biweekly: 'Bi-weekly',
        monthly: 'Monthly',
        none: 'None',
      },
      typeByMedicalRole: {
        therapist: 'Therapy'
      },
      shortType: {
        therapy: 'Therapy',
        psychiatry: 'Psychiatry',
      },
      types: {
        therapy: 'Psychotherapy Session',
        psychiatry: 'Psychiatry Consultation',
      },
    },
    agreementTemplate: {
      types: {
        none: 'None',
        collaborative_practice_agreement: 'Collaborative Practice Agreement',
      }
    }
  },
  shared: {
    ClickToCopy: {
      copy: 'Copy',
      copied: 'Copied!',
    },
    charts: {
      ProgressChart: {
        details: 'Details',
        weeks: 'Weeks',
        last6Months: 'Last 6 Months',
      }
    }
  },
  publicProfile: {
    share: {
      dashboard: {
        card: {
          title: 'Grow your client list',
          description: 'Share your availability with potential clients so they can easily book appointments anytime.',
          buttonText: 'Copy profile link',
        },
      },
    },
    footer: {
      header: 'About UpLift',
      line1: "We're rethinking mental health to work for everyone. We believe it should be simple for everyone to access affordable care and experience real growth.",
      line2: 'UpLift makes it easy for you to pay for therapy sessions using your insurance or with cash at an affordable rate.',
      findATherapist: 'Find a Therapist',
    }
  },
  businessEntity: {
    name: 'Entity name is required',
    tin: 'TIN is required',
    npi: 'NPI is required',
    taxonomy: 'Taxonomy is required',
  },
  providerTermination: {
    voluntary: 'Voluntary',
    involuntary: 'Involuntary',
    deceased: 'Deceased',
    provider_request: 'Provider Request',
    retirement: 'Retirement',
    personal_circumstances: 'Personal Circumstances',
    pursuing_other_opportunities: 'Pursuing Other Opportunities',
    quality: 'Quality',
    communication: 'Communication',
    policy_violation: 'Policy Violation',
    recredentialing_deadline_missed: 'Recredentialing Deadline Missed',
    credentialing_committee_denial: 'Credentialing Committee Denial',
    licensing_issue: 'Licensing Issue',
    conduct_boundary_violation: 'Conduct / Boundary Violation',
    unresponsive_provider: 'Unresponsive Provider',
  },
} as const;
export type L10NShape = typeof l10nEnService;
